// The pieces the andon thread and its cards are built from. Lifted from the
// Andon app's `primitives.tsx`, minus what the shop floor already has.
//
// `StatusRow`, `Toast` and `SectionLabel` did not come across: the shop
// floor's header carries the connection and the instance, its `Toast` is the
// shell's, and `SectionLabel` in `ui/misc` draws the same label in the app's
// own type. What is here is what had no twin — the star chip, the SLA bar and
// the owner avatar, whose red "?" for nobody is a meaning `AvatarTile` does
// not carry.

import { useEffect, useState, type CSSProperties } from 'react';
import type { SlaVM, Stars } from '@/api/andon/types';
import { initials } from '@/components/ui/misc';
import { useT } from '@/i18n/useT';
import { STAR_COLOR, STAR_TINT, starLabel } from '@/lib/severity';
import { clock } from '@/lib/time';

/** A star tier as a coloured chip: ★★★ in the tier's own colour. */
export function StarChip({ stars, style }: { stars: Stars; style?: CSSProperties }) {
  return (
    <span
      style={{
        padding: '3px 8px',
        borderRadius: 7,
        background: STAR_TINT[stars],
        color: STAR_COLOR[stars],
        font: "700 11px 'Space Grotesk',sans-serif",
        letterSpacing: '.04em',
        ...style,
      }}
    >
      {starLabel(stars)}
    </span>
  );
}

/**
 * The SLA bar and its countdown. It reads down to zero and then keeps going
 * negative — a breach that quietly stops counting is a breach nobody feels.
 *
 * Against `sla.dueAt` rather than `raisedAt + totalMs`: the server decides
 * WHETHER a clock is running and WHEN it runs out; the client only subtracts,
 * every second, so the bar keeps moving between polls instead of stepping.
 */
export function SlaBar({ sla, stars, now }: { sla: SlaVM; stars: Stars; now: number }) {
  const t = useT();
  const remaining = sla.msLeft == null || sla.dueAt == null ? null : sla.dueAt - now;
  const fraction = remaining == null ? 1 : Math.max(0, Math.min(1, remaining / sla.totalMs));
  const overdue = remaining != null && remaining <= 0;
  const color = remaining == null ? 'var(--sub)' : overdue ? 'var(--danger)' : STAR_COLOR[stars];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--line)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.max(4, fraction * 100)}%`, background: color, borderRadius: 3, transition: 'width 1s linear' }} />
      </div>
      <span className="numeric" style={{ font: "700 13px 'Space Grotesk',sans-serif", color }}>
        {remaining == null ? t.slaHeldT : overdue ? t.slaEscT : clock(remaining)}
      </span>
    </div>
  );
}

/**
 * The owner. One name, always — or a red "?" and the colour that gets an
 * andon picked up fastest.
 *
 * Kept beside `AvatarTile` rather than folded into it: that one draws a
 * person, and this one also draws the ABSENCE of a person, which on a card
 * about a stopped line is the more important of the two.
 */
export function OwnerAvatar({
  name,
  size = 38,
  photoUrl,
  round = false,
  background,
  color,
}: {
  name: string | null;
  size?: number;
  /** Odoo's photo of this person, if we know an id to ask for one. */
  photoUrl?: string | undefined;
  /**
   * A circle rather than the rounded square.
   *
   * The thread's avatars are round and everything else's are not — a bubble
   * carries a person mid-sentence where a card carries an owner.
   */
  round?: boolean;
  background?: string;
  color?: string;
}) {
  const unowned = !name;
  // Odoo holds a photo for only some employees, and permission can be refused
  // per account — so fall back on the error rather than trying to know in
  // advance, which keeps the initials correct for every reason the image can
  // fail rather than only the one we predicted.
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [photoUrl]);
  const showPhoto = !!photoUrl && !failed && !unowned;

  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: round ? '50%' : Math.round(size * 0.32),
        display: 'grid',
        placeItems: 'center',
        flex: 'none',
        overflow: 'hidden',
        background: unowned ? 'var(--danger)' : (background ?? 'var(--surface2)'),
        color: unowned ? '#fff' : (color ?? 'var(--ink)'),
        font: `700 ${Math.max(11, Math.round(size * 0.36))}px 'Space Grotesk',sans-serif`,
      }}
    >
      {showPhoto ? (
        <img src={photoUrl} alt="" onError={() => setFailed(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        initials(name ?? '')
      )}
    </span>
  );
}
